// src/components/subscription/ResourceQuotaAlert.jsx
import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { apiCall } from '../../utils/api';

const ResourceQuotaAlert = ({ user }) => {
  const [visible, setVisible] = useState(false);
  const [resourceWarnings, setResourceWarnings] = useState([]);
  
  // Fetch resource data
  useEffect(() => {
    if (!user) return;
    
    const fetchData = async () => {
      try {
        // First check if we can fetch the subscription
        const subResponse = await apiCall(`/api/subscriptions/user/${user.uid}?app_id=saleswit`);
        if (!subResponse.ok) return;
        
        const subData = await subResponse.json();
        const subscription = subData.subscription;
        if (!subscription || !subscription.features) return;
        
        // Then fetch resource quota
        const quotaResponse = await apiCall(
          `/api/subscriptions/resource-quota?user_id=${user.uid}&app_id=saleswit`
        );
        
        if (!quotaResponse.ok) return;
        
        const quotaData = await quotaResponse.json();
        const resourceQuota = quotaData.quota || {};
        
        // Calculate warnings
        const warnings = [];
        
        // Check document_pages usage
        if (subscription.features.document_pages) {
          const limit = subscription.features.document_pages;
          const remaining = resourceQuota.document_pages || 0;
          const used = limit - remaining;
          const percentage = (used / limit) * 100;
          
          if (percentage >= 80) {
            warnings.push({
              type: 'document_pages',
              percentage: Math.round(percentage),
              remaining: remaining,
              limit: limit
            });
          }
        }
        
        // Check perplexity_requests usage
        if (subscription.features.perplexity_requests) {
          const limit = subscription.features.perplexity_requests;
          const remaining = resourceQuota.perplexity_requests || 0;
          const used = limit - remaining;
          const percentage = (used / limit) * 100;
          
          if (percentage >= 80) {
            warnings.push({
              type: 'perplexity_requests',
              percentage: Math.round(percentage),
              remaining: remaining,
              limit: limit
            });
          }
        }
        
        // Update state with any warnings
        setResourceWarnings(warnings);
        setVisible(warnings.length > 0);
      } catch (error) {
        console.error('Error fetching data for resource alerts:', error);
        // Don't crash the app on error
      }
    };
    
    fetchData();
  }, [user]);
  
  // Format the resource type for display
  const formatResourceType = (type) => {
    switch(type) {
      case 'document_pages':
        return 'Document Pages';
      case 'perplexity_requests':
        return 'AI Generation Requests';
      case 'requests':
        return 'Reports';        
      default:
        return type;
    }
  };
  
  if (!visible || resourceWarnings.length === 0) return null;
  
  return (
    <div className="fixed bottom-4 left-4 z-50 max-w-md bg-[#1e293b] rounded-lg shadow-xl border border-amber-500 p-4">
      <div className="flex items-start">
        <div className="flex-shrink-0 mt-0.5">
          <AlertTriangle className="h-5 w-5 text-amber-400" />
        </div>
        <div className="ml-3 flex-1 pr-6">
          <h3 className="text-sm font-medium text-white">Resource Usage Alert</h3>
          <div className="mt-2 text-sm text-gray-300 space-y-2">
            {resourceWarnings.map((warning, index) => (
              <p key={index}>
                You have used <span className="font-medium text-amber-400">{warning.percentage}%</span> of 
                your {formatResourceType(warning.type)}.
              </p>
            ))}
          </div>
          <div className="mt-3">
            <a 
              href="/subscription-dashboard" 
              className="text-xs text-amber-400 hover:text-amber-300 font-medium"
            >
              View subscription details →
            </a>
          </div>
        </div>
        <button 
          onClick={() => setVisible(false)}
          className="absolute top-3 right-3 text-gray-400 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default ResourceQuotaAlert;